import React, { useState, useCallback, useEffect } from 'react';
import { TeamDetail } from '../../../types/settings/TeamTypes';
import TeamForm from '../../../components/Forms/TeamForm';
import { getTeams, deleteTeam } from '@/actions/data-fetch-teams';
import { toastMessage } from '@/utils/toast-message';
import ModernTeamsTable from './ModernTeamsTable';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';
import { Users } from 'lucide-react';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

interface TeamsClientWrapperProps {
    initialData?: TeamDetail[];
    pagination?: PaginationProps;
}

export const TeamsClientWrapper: React.FC<TeamsClientWrapperProps> = ({
    initialData = [],
    pagination
}) => {
    const [selectedTeam, setSelectedTeam] = useState<TeamDetail | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [data, setData] = useState<TeamDetail[]>(initialData);
    const [currentPagination, setCurrentPagination] = useState<PaginationProps | undefined>(pagination);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortColumn, setSortColumn] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

    const fetchData = useCallback(async (params: {
        page?: number;
        search_term?: string;
        sort_column?: string;
        sort_order?: 'ASC' | 'DESC';
    } = {}) => {
        try {
            setIsLoading(true);
            const response = await getTeams({
                page: params.page || 1,
                search_term: params.search_term,
                sort_column: params.sort_column,
                sort_order: params.sort_order,
                includeMembers: true,
                limit: 20
            });

            if (response.success || response.status === 'success') {
                const responseData = response.data;
                setData(responseData?.teams || []);
                if (responseData?.pagination) {
                    setCurrentPagination({
                        currentPage: responseData.pagination.currentPage || params.page || 1,
                        totalPages: responseData.pagination.totalPages || 1,
                        totalItems: responseData.pagination.totalItems || 0,
                        itemsPerPage: responseData.pagination.itemsPerPage || 20,
                        hasNextPage: responseData.pagination.hasNextPage || false,
                        hasPreviousPage: responseData.pagination.hasPreviousPage || false
                    });
                }
            }
        } catch (error: unknown) {
            if (error instanceof Error) {
                toastMessage(error.message, 'error');
            } else {
                toastMessage('An unknown error occurred', 'error');
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        // Only fetch if we don't have initial data
        if (!initialData || initialData.length === 0) {
            fetchData();
        }
    }, []);

    const handleEdit = (team?: TeamDetail) => {
        setSelectedTeam(team || null);
        setIsModalOpen(true);
    };

    const handleClose = () => {
        setIsModalOpen(false);
        setSelectedTeam(null);
    };

    const handleOpenModal = (team?: TeamDetail) => {
        setSelectedTeam(team || null);
        setIsModalOpen(true);
    };

    const handleSuccess = async (updatedTeam: TeamDetail) => {
        // Update local state optimistically
        setData(prevData => {
            const index = prevData.findIndex(t => t.uuid === updatedTeam.uuid);
            if (index !== -1) {
                // Update existing team
                const newData = [...prevData];
                newData[index] = updatedTeam;
                return newData;
            } else {
                // Add new team
                return [...prevData, updatedTeam];
            }
        });

        // Close modal
        handleClose();

        // Refresh data from server to ensure consistency
        await fetchData({
            page: currentPagination?.currentPage || 1,
            search_term: searchTerm,
            sort_column: sortColumn,
            sort_order: sortOrder
        });

        toastMessage(
            selectedTeam ? 'Team updated successfully' : 'Team created successfully',
            'success'
        );
    };

    // @ts-expect-error - Function reserved for future delete functionality
    const handleDelete = async (team: TeamDetail) => {
        if (!confirm(`Are you sure you want to delete "${team.team_name}"? This action cannot be undone.`)) {
            return;
        }

        try {
            const response = await deleteTeam(team.uuid);

            if (response.success || response.status === 'success') {
                toastMessage('Team deleted successfully', 'success');

                // Remove from local state
                setData(prevData => prevData.filter(t => t.uuid !== team.uuid));

                // Refresh from server
                await fetchData({
                    page: currentPagination?.currentPage || 1,
                    search_term: searchTerm,
                    sort_column: sortColumn,
                    sort_order: sortOrder
                });
            } else {
                toastMessage(response.message || 'Failed to delete team', 'error');
            }
        } catch (error: any) {
            toastMessage(error.message || 'Failed to delete team', 'error');
        }
    };

    const handlePageChange = async (page: number) => {
        await fetchData({
            page,
            search_term: searchTerm,
            sort_column: sortColumn,
            sort_order: sortOrder
        });
    };

    const handleSearch = async (term: string): Promise<void> => {
        setSearchTerm(term);
        await fetchData({
            page: 1,
            search_term: term,
            sort_column: sortColumn,
            sort_order: sortOrder
        });
    };

    const handleSort = async (column: string, order: 'ASC' | 'DESC'): Promise<void> => {
        setSortColumn(column);
        setSortOrder(order);
        await fetchData({
            page: currentPagination?.currentPage || 1,
            search_term: searchTerm,
            sort_column: column,
            sort_order: order
        });
    };

    return (
        <div className="h-full flex flex-col">
            <div className="sticky top-0 z-10">
                <ModernSettingHeader
                    title="Team Management"
                    description="Organize your workforce into teams for better collaboration and task management. Assign team leaders and manage member access."
                    buttonText="Create New Team"
                    onClick={() => handleOpenModal()}
                    icon={<Users className="w-6 h-6" />}
                />
            </div>
            <div className="flex-1 overflow-y-auto p-6">
                <ModernTeamsTable
                    initialData={data}
                    onEdit={handleEdit}
                    pagination={currentPagination}
                    onPageChange={handlePageChange}
                    onSearch={handleSearch}
                    onSort={handleSort}
                    onRefresh={() => fetchData({
                        page: currentPagination?.currentPage || 1,
                        search_term: searchTerm,
                        sort_column: sortColumn,
                        sort_order: sortOrder
                    })}
                    isLoading={isLoading}
                    searchTerm={searchTerm}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                />
            </div>

            {/* Modal/Sidebar Form */}
            <SettingsSlideModal
                isOpen={isModalOpen}
                onClose={handleClose}
                title={selectedTeam ? 'Edit Team' : 'Create New Team'}
                subtitle={selectedTeam ? 'Update team details and member assignments' : 'Set up a new team for better organization and collaboration'}
                icon={<Users className="w-6 h-6" />}
            >
                <TeamForm
                    uuid={selectedTeam?.uuid}
                    initialData={selectedTeam}
                    onSuccess={handleSuccess}
                    onClose={handleClose}
                />
            </SettingsSlideModal>
        </div>
    );
};

export default TeamsClientWrapper;